import React, { useEffect, useState, useRef, lazy, Component } from 'react';
import { motion } from 'framer-motion';
import { FilterIcon, HomeIcon, LoaderIcon } from 'lucide-react';
import { Footer } from '../components/Footer';
import { SubpageNavbar } from '../components/SubpageNavbar';
import { Unit, formatArea } from '../data/units';
import { useUnits } from '../hooks/useUnits';
type FilterStatus = 'all' | 'k dispozici' | 'prodáno' | 'rezervováno';
type FilterKategorie = 'all' | '1+kk' | '2+kk' | '3+kk' | '4+kk';
type FilterPatro = 'all' | '1NP' | '2NP' | '3NP';
export function UnitsPage() {
  const {
    units,
    isLoading,
    error
  } = useUnits();
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('all');
  const [kategorieFilter, setKategorieFilter] = useState<FilterKategorie>('all');
  const [patroFilter, setPatroFilter] = useState<FilterPatro>('all');
  const [iframeHeight, setIframeHeight] = useState(800);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Listen for messages from the iframe to auto-resize
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin === 'https://static.svatyvaclav.living' && event.data?.type === 'resize') {
        setIframeHeight(event.data.height);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);
  const filteredUnits = units.filter(unit => {
    if (statusFilter !== 'all' && unit.stav !== statusFilter) return false;
    if (kategorieFilter !== 'all' && unit.kategorie !== kategorieFilter) return false;
    if (patroFilter !== 'all' && unit.patro !== patroFilter) return false;
    return true;
  });
  const getStatusBadge = (stav: Unit['stav']) => {
    switch (stav) {
      case 'k dispozici':
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800" data-id="element-324">
            K dispozici
          </span>;
      case 'rezervováno':
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800" data-id="element-325">
            Rezervováno
          </span>;
      case 'prodáno':
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800" data-id="element-326">
            Prodáno
          </span>;
    }
  };
  const handleContactClick = (unitKod: string) => {
    const message = `Dobrý den, mám zájem o byt ${unitKod}. Prosím o zaslání více informací a aktuálního ceníku.`;
    window.location.hash = `kontakt?byt=${encodeURIComponent(unitKod)}&zprava=${encodeURIComponent(message)}`;
  };
  return <div className="min-h-screen bg-base-cream font-body text-base-dark flex flex-col" data-id="element-327">
      <SubpageNavbar currentPage="nabidka-bytu" data-id="element-328" />

      <main className="flex-grow py-16" data-id="element-329">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-330">
          {/* Header */}
          <div className="text-center mb-12" data-id="element-331">
            <h1 className="font-heading text-4xl md:text-5xl text-primary mb-4" data-id="element-332">
              Nabídka bytů
            </h1>
            <p className="text-base-gray max-w-2xl mx-auto" data-id="element-333">
              Kompletní přehled všech bytových jednotek v projektu Svatý Václav.
            </p>
          </div>
        </div>

        {/* External Floor Plan Widget - Full width breakout */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="mb-10 overflow-hidden hidden md:block" data-id="element-334">
          <div style={{
          position: 'relative',
          width: '100%',
          paddingBottom: '56.18%'
        }} data-id="element-335">
            <iframe ref={iframeRef} src="https://static.svatyvaclav.living/" title="Interaktivní výběr bytů – Svatý Václav" className="border-0" style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            overflowX: 'hidden'
          }} scrolling="no" allow="fullscreen" loading="lazy" data-id="element-336" />
          </div>
        </motion.div>

        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-337">
          {/* Filters */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4
        }} className="bg-white rounded-xl p-6 shadow-sm mb-8" data-id="element-338">
            <div className="flex items-center gap-2 mb-4" data-id="element-339">
              <FilterIcon className="w-5 h-5 text-primary" data-id="element-340" />
              <span className="font-medium text-base-dark" data-id="element-341">Filtrovat</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-342">
              <div data-id="element-343">
                <label className="block text-sm text-base-gray mb-2" data-id="element-344">
                  Stav
                </label>
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as FilterStatus)} className="w-full bg-base-cream border border-gray-200 rounded-lg px-4 py-2.5 text-base-dark focus:outline-none focus:border-primary transition-colors" data-id="element-345">
                  <option value="all" data-id="element-346">Všechny</option>
                  <option value="k dispozici" data-id="element-347">K dispozici</option>
                  <option value="rezervováno" data-id="element-348">Rezervováno</option>
                  <option value="prodáno" data-id="element-349">Prodáno</option>
                </select>
              </div>
              <div data-id="element-350">
                <label className="block text-sm text-base-gray mb-2" data-id="element-351">
                  Dispozice
                </label>
                <select value={kategorieFilter} onChange={e => setKategorieFilter(e.target.value as FilterKategorie)} className="w-full bg-base-cream border border-gray-200 rounded-lg px-4 py-2.5 text-base-dark focus:outline-none focus:border-primary transition-colors" data-id="element-352">
                  <option value="all" data-id="element-353">Všechny</option>
                  <option value="1+kk" data-id="element-354">1+kk</option>
                  <option value="2+kk" data-id="element-355">2+kk</option>
                  <option value="3+kk" data-id="element-356">3+kk</option>
                  <option value="4+kk" data-id="element-357">4+kk</option>
                </select>
              </div>
              <div data-id="element-358">
                <label className="block text-sm text-base-gray mb-2" data-id="element-359">
                  Podlaží
                </label>
                <select value={patroFilter} onChange={e => setPatroFilter(e.target.value as FilterPatro)} className="w-full bg-base-cream border border-gray-200 rounded-lg px-4 py-2.5 text-base-dark focus:outline-none focus:border-primary transition-colors" data-id="element-360">
                  <option value="all" data-id="element-361">Všechny</option>
                  <option value="1NP" data-id="element-362">1. NP</option>
                  <option value="2NP" data-id="element-363">2. NP</option>
                  <option value="3NP" data-id="element-364">3. NP</option>
                </select>
              </div>
            </div>
          </motion.div>

          {/* Results count */}
          <div className="mb-6 text-base-gray" data-id="element-365">
            Nalezeno{' '}
            <strong className="text-base-dark" data-id="element-366">{filteredUnits.length}</strong>{' '}
            bytů
          </div>

          {/* Table - Desktop */}
          <div className="hidden lg:block overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm" data-id="element-367">
            <table className="w-full" data-id="element-368">
              <thead data-id="element-369">
                <tr className="bg-primary text-white" data-id="element-370">
                  <th className="px-4 py-4 text-left font-medium text-sm" data-id="element-371">
                    Kód
                  </th>
                  <th className="px-4 py-4 text-left font-medium text-sm" data-id="element-372">
                    Podlaží
                  </th>
                  <th className="px-4 py-4 text-left font-medium text-sm" data-id="element-373">
                    Dispozice
                  </th>
                  <th className="px-4 py-4 text-right font-medium text-sm" data-id="element-374">
                    Celková plocha
                    <br data-id="element-375" />
                    jednotky
                  </th>
                  <th className="px-4 py-4 text-right font-medium text-sm" data-id="element-376">
                    Podlahová plocha
                    <br data-id="element-377" />
                    bytu
                  </th>
                  <th className="px-4 py-4 text-right font-medium text-sm" data-id="element-378">
                    Terasa
                  </th>
                  <th className="px-4 py-4 text-right font-medium text-sm" data-id="element-379">
                    Předzahrádka
                  </th>
                  <th className="px-4 py-4 text-center font-medium text-sm" data-id="element-380">
                    Stav
                  </th>
                  <th className="px-4 py-4 text-center font-medium text-sm" data-id="element-381">
                    Cena
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-382">
                {filteredUnits.map((unit, index) => <motion.tr key={unit.id} initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                duration: 0.3,
                delay: index * 0.02
              }} className={`border-b border-gray-100 last:border-none ${unit.stav === 'prodáno' ? 'bg-gray-50 text-base-gray' : 'hover:bg-base-cream/50'}`} data-id="element-383">
                    <td className="px-4 py-4 font-medium text-base-dark" data-id="element-384">
                      {unit.kod}
                    </td>
                    <td className="px-4 py-4" data-id="element-385">{unit.patro}</td>
                    <td className="px-4 py-4 font-medium" data-id="element-386">{unit.kategorie}</td>
                    <td className="px-4 py-4 text-right font-medium text-base-dark" data-id="element-387">
                      {formatArea(unit.uzitnaPlochM2 + (unit.terasaM2 ?? 0))}
                    </td>
                    <td className="px-4 py-4 text-right" data-id="element-388">
                      {formatArea(unit.uzitnaPlochM2)}
                    </td>
                    <td className="px-4 py-4 text-right" data-id="element-389">
                      {formatArea(unit.terasaM2)}
                    </td>
                    <td className="px-4 py-4 text-right" data-id="element-390">
                      {formatArea(unit.predzahradkaM2)}
                    </td>
                    <td className="px-4 py-4 text-center" data-id="element-391">
                      {getStatusBadge(unit.stav)}
                    </td>
                    <td className="px-4 py-4 text-center" data-id="element-392">
                      {unit.stav === 'k dispozici' ? <button onClick={() => handleContactClick(unit.kod)} className="text-primary hover:text-accent font-medium text-sm underline underline-offset-2 transition-colors" data-id="element-393">
                          Kontaktujte nás
                        </button> : <span className="text-base-gray text-sm" data-id="element-394">—</span>}
                    </td>
                  </motion.tr>)}
              </tbody>
            </table>
          </div>

          {/* Cards - Mobile/Tablet */}
          <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-395">
            {filteredUnits.map((unit, index) => <motion.div key={unit.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            delay: index * 0.05
          }} className={`bg-white rounded-xl p-5 shadow-sm border border-gray-100 ${unit.stav === 'prodáno' ? 'opacity-70' : ''}`} data-id="element-396">
                <div className="flex items-start justify-between mb-4" data-id="element-397">
                  <div className="flex items-center gap-3" data-id="element-398">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center" data-id="element-399">
                      <HomeIcon className="w-5 h-5 text-primary" data-id="element-400" />
                    </div>
                    <div data-id="element-401">
                      <h3 className="font-heading text-lg font-bold text-base-dark" data-id="element-402">
                        {unit.kod}
                      </h3>
                      <p className="text-sm text-base-gray" data-id="element-403">{unit.kategorie}</p>
                    </div>
                  </div>
                  {getStatusBadge(unit.stav)}
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm mb-4" data-id="element-404">
                  <div data-id="element-405">
                    <span className="text-base-gray" data-id="element-406">Podlaží:</span>
                    <span className="ml-2 font-medium text-base-dark" data-id="element-407">
                      {unit.patro}
                    </span>
                  </div>
                  <div data-id="element-408">
                    <span className="text-base-gray" data-id="element-409">Plocha:</span>
                    <span className="ml-2 font-medium text-base-dark" data-id="element-410">
                      {formatArea(unit.uzitnaPlochM2)}
                    </span>
                  </div>
                  {unit.terasaM2 && <div data-id="element-411">
                      <span className="text-base-gray" data-id="element-412">Terasa:</span>
                      <span className="ml-2 font-medium text-base-dark" data-id="element-413">
                        {formatArea(unit.terasaM2)}
                      </span>
                    </div>}
                  {unit.predzahradkaM2 && <div data-id="element-414">
                      <span className="text-base-gray" data-id="element-415">Předzahrádka:</span>
                      <span className="ml-2 font-medium text-base-dark" data-id="element-416">
                        {formatArea(unit.predzahradkaM2)}
                      </span>
                    </div>}
                </div>

                {unit.stav === 'k dispozici' && <button onClick={() => handleContactClick(unit.kod)} className="w-full bg-primary hover:bg-primary-light text-white py-3 rounded-lg font-medium transition-colors" data-id="element-417">
                    Kontaktujte nás
                  </button>}
              </motion.div>)}
          </div>

          {filteredUnits.length === 0 && <div className="text-center py-16" data-id="element-418">
              <p className="text-base-gray text-lg" data-id="element-419">
                Žádné byty neodpovídají vybraným filtrům.
              </p>
            </div>}
        </div>
      </main>

      <Footer data-id="element-420" />
    </div>;
}